import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Banknote } from 'lucide-react';
import { matchingAPI } from '../../lib/api';

export function ConfirmedMatchPaymentBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const res = await matchingAPI.previewPaymentBackfill();
      setPreview(res.data);
    } catch {
      setPreview({ error: 'Failed to run preview.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      `Stamp payment status on ${preview?.invoice_count || 0} invoice(s) with a pre-existing ` +
      'confirmed match? Each is stamped paid/partially_paid using the transaction that confirmed it.'
    )) return;
    setApplyLoading(true);
    try {
      const res = await matchingAPI.applyPaymentBackfill();
      setApplyResult(res.data);
    } catch {
      setApplyResult({ error: 'Failed to apply backfill.' });
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Banknote className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Confirmed Match Payment Backfill</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Stamps payment_status/paid_at/payment_source on invoices that already have a confirmed
          reconciliation match from before that confirm path stamped payment status. Two-step: Preview, then Apply.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runPreview} disabled={previewLoading} data-testid="payment-backfill-preview">
            {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && preview.invoice_count > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyLoading || !!applyResult}
              data-testid="payment-backfill-apply"
            >
              {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply to {preview.invoice_count} Invoices
            </Button>
          )}
        </div>

        {preview && (
          <>
            {preview.error ? (
              <p className="text-xs text-red-600">{preview.error}</p>
            ) : (
              <div className="space-y-2" data-testid="payment-backfill-preview-result">
                <div className="flex flex-wrap gap-3 text-xs">
                  <span>Invoices to stamp: <strong>{preview.invoice_count}</strong></span>
                  {preview.multi_confirmed_invoice_count > 0 && (
                    <span className="text-amber-600">
                      Double-confirmed (pre-existing anomaly): <strong>{preview.multi_confirmed_invoice_count}</strong>
                    </span>
                  )}
                  {preview.self_heal_resumable_count > 0 && (
                    <span className="text-amber-600">
                      Also resumable via Confirm-Stepper Self-Heal: <strong>{preview.self_heal_resumable_count}</strong>
                    </span>
                  )}
                </div>
                {preview.by_confirmed_via && Object.keys(preview.by_confirmed_via).length > 0 && (
                  <div className="flex flex-wrap gap-2 text-xs" data-testid="payment-backfill-by-confirmed-via">
                    {Object.entries(preview.by_confirmed_via).map(([via, count]) => (
                      <Badge key={via} variant="outline" className="text-[10px]">
                        {via}: {count}
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">{preview.message}</p>
              </div>
            )}
          </>
        )}

        {applyResult && (
          <div className={`p-3 rounded text-xs border ${applyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`} data-testid="payment-backfill-apply-result">
            {applyResult.error ? (
              <p className="text-red-600">{applyResult.error}</p>
            ) : (
              <div className="space-y-1">
                <p><strong>Stamped:</strong> {applyResult.stamped} of {applyResult.invoice_count}</p>
                {applyResult.failed && applyResult.failed.length > 0 && (
                  <p className="text-red-600"><strong>Failed:</strong> {applyResult.failed.length} (see backend log for reasons)</p>
                )}
                {applyResult.multi_confirmed_invoice_count > 0 && (
                  <Badge variant="outline" className="text-[10px]">
                    {applyResult.multi_confirmed_invoice_count} double-confirmed invoice(s) resolved to their most recent match
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
